import React, { useState, useEffect } from 'react';
import {
  Phone, Shield, Key, Copy, Check, Eye, EyeOff, RefreshCw,
  CheckCircle, XCircle, AlertCircle, ExternalLink, ArrowLeftRight,
  Wifi, WifiOff, Clipboard
} from 'lucide-react';
import api from '../../services/api';

function TelnyxConfig() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [sipData, setSipData] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [currentKeyMask, setCurrentKeyMask] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [provider, setProvider] = useState('ringcentral');
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    loadStatus();
    loadSipData();
    loadProvider();
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await api.get('/telnyx/status');
      setStatus(res.data);
    } catch {
      setStatus({ status: 'error' });
    } finally {
      setStatusLoading(false);
    }
  };

  const loadSipData = async () => {
    try {
      const res = await api.get('/telnyx/sip-provision');
      setSipData(res.data);
    } catch {
      // Not configured yet
    }
  };

  const loadProvider = async () => {
    try {
      const res = await api.get('/general');
      setProvider(res.data.phone_provider || 'ringcentral');
    } catch {
      // Default to RC
    }
  };

  const loadConfig = async () => {
    try {
      const res = await api.get('/telnyx/config');
      if (res.data?.api_key_masked) {
        setCurrentKeyMask(res.data.api_key_masked);
      }
    } catch {
      // Not configured
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/telnyx/config', { api_key: apiKey });
      setSuccess('API key saved successfully');
      setApiKey('');
      loadConfig();
      loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleProviderToggle = async () => {
    const newProvider = provider === 'telnyx' ? 'ringcentral' : 'telnyx';
    const confirmed = window.confirm(
      `Switch phone provider to ${newProvider === 'telnyx' ? 'Telnyx' : 'RingCentral'}?\n\n` +
      'Switching providers will affect all active call routing. ' +
      'Make sure the target provider is fully configured before switching.'
    );
    if (!confirmed) return;

    setSwitching(true);
    setError('');
    try {
      await api.put('/general', { phone_provider: newProvider });
      setProvider(newProvider);
      setSuccess(`Switched to ${newProvider === 'telnyx' ? 'Telnyx' : 'RingCentral'}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to switch provider');
    } finally {
      setSwitching(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ value, field }) => (
    <button
      onClick={() => copyToClipboard(value, field)}
      className="p-1.5 rounded-lg hover:bg-virgo-navy-600 transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-virgo-teal-500" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent">
              Telnyx Configuration
            </h2>
            <p className="text-virgo-teal-400 text-sm mt-0.5">Configure Telnyx for VoIP calling and SMS</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {statusLoading ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg">
              <RefreshCw className="w-4 h-4 text-virgo-teal-500 animate-spin" />
              <span className="text-virgo-teal-400 text-sm font-medium">Checking...</span>
            </span>
          ) : status?.status === 'connected' ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
              <Wifi className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Connected</span>
            </span>
          ) : status?.status === 'error' ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm font-medium">Error</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg">
              <WifiOff className="w-4 h-4 text-virgo-teal-500" />
              <span className="text-virgo-teal-400 text-sm font-medium">Not Configured</span>
            </span>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {/* Connection Status Card */}
      {status && status.status !== 'not_implemented' && (
        <div className="bg-virgo-navy-700/50 border border-virgo-navy-600 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-cyan-400" />
            Connection Status
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {status.connection_id && (
              <div>
                <span className="text-virgo-teal-500 text-xs uppercase tracking-wider">Connection ID</span>
                <p className="text-white text-sm mt-1 font-mono">{status.connection_id}</p>
              </div>
            )}
            {status.phone_number && (
              <div>
                <span className="text-virgo-teal-500 text-xs uppercase tracking-wider">Phone Number</span>
                <p className="text-white text-sm mt-1 font-mono">{status.phone_number}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Credentials Card */}
      <div className="bg-virgo-navy-700/50 border border-virgo-navy-600 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Key className="w-4 h-4 text-cyan-400" />
          API Credentials
        </h3>
        {currentKeyMask && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-virgo-teal-500 text-sm">Current key:</span>
            <span className="text-white font-mono text-sm bg-virgo-navy-600 px-3 py-1 rounded">
              {currentKeyMask}
            </span>
          </div>
        )}
        <div className="flex gap-3">
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Enter Telnyx API Key (KEY...)"
            className="flex-1 px-4 py-2.5 bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg text-white placeholder-virgo-teal-600 focus:outline-none focus:border-cyan-500 text-sm font-mono"
          />
          <button
            onClick={handleSaveApiKey}
            disabled={saving || !apiKey.trim()}
            className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-lg font-medium text-sm hover:from-cyan-400 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Save Key
          </button>
        </div>
        <p className="text-virgo-teal-600 text-xs mt-2">
          Get your API key from the{' '}
          <a href="https://portal.telnyx.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
            Telnyx Mission Control Portal
          </a>
          {' '}→ Auth V2
        </p>
      </div>

      {/* SIP Credentials Card */}
      <div className="bg-virgo-navy-700/50 border border-virgo-navy-600 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Phone className="w-4 h-4 text-cyan-400" />
          SIP Credentials
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-virgo-navy-800 rounded-lg px-4 py-3">
            <div>
              <span className="text-virgo-teal-500 text-xs uppercase tracking-wider block">SIP Domain</span>
              <span className="text-white text-sm font-mono mt-0.5 block">
                {sipData?.sip_domain || 'sip.telnyx.com'}
              </span>
            </div>
            <CopyButton value={sipData?.sip_domain || 'sip.telnyx.com'} field="domain" />
          </div>
          <div className="flex items-center justify-between bg-virgo-navy-800 rounded-lg px-4 py-3">
            <div>
              <span className="text-virgo-teal-500 text-xs uppercase tracking-wider block">Username</span>
              <span className="text-white text-sm font-mono mt-0.5 block">
                {sipData?.username || '—'}
              </span>
            </div>
            {sipData?.username && <CopyButton value={sipData.username} field="username" />}
          </div>
          <div className="flex items-center justify-between bg-virgo-navy-800 rounded-lg px-4 py-3">
            <div>
              <span className="text-virgo-teal-500 text-xs uppercase tracking-wider block">Password</span>
              <span className="text-white text-sm font-mono mt-0.5 block">
                {sipData?.password
                  ? showPassword ? sipData.password : '••••••••••••'
                  : '—'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {sipData?.password && (
                <>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1.5 rounded-lg hover:bg-virgo-navy-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-3.5 h-3.5 text-virgo-teal-500" />
                    ) : (
                      <Eye className="w-3.5 h-3.5 text-virgo-teal-500" />
                    )}
                  </button>
                  <CopyButton value={sipData.password} field="password" />
                </>
              )}
            </div>
          </div>
        </div>
        <p className="text-virgo-teal-600 text-xs mt-3">
          These credentials are auto-configured in the softphone. Only needed if setting up a hardware phone.
        </p>
      </div>

      {/* Provider Toggle Card */}
      <div className="bg-virgo-navy-700/50 border border-virgo-navy-600 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
          Phone Provider
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-virgo-teal-500 text-xs uppercase tracking-wider block">Active Provider</span>
            <span className={`text-lg font-semibold mt-1 block ${
              provider === 'telnyx' ? 'text-emerald-400' : 'text-cyan-400'
            }`}>
              {provider === 'telnyx' ? 'Telnyx' : 'RingCentral'}
            </span>
          </div>
          <button
            onClick={handleProviderToggle}
            disabled={switching}
            className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              provider === 'telnyx'
                ? 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20'
                : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
            } disabled:opacity-50`}
          >
            {switching ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowLeftRight className="w-4 h-4" />
            )}
            Switch to {provider === 'telnyx' ? 'RingCentral' : 'Telnyx'}
          </button>
        </div>
      </div>

      {/* Number Porting Card */}
      <div className="bg-virgo-navy-700/50 border border-virgo-navy-600 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Clipboard className="w-4 h-4 text-cyan-400" />
          Number Porting
        </h3>
        <p className="text-virgo-teal-400 text-sm leading-relaxed">
          To port your existing number to Telnyx, visit the Telnyx Mission Control portal under Numbers → Port Numbers.
        </p>
        <a
          href="https://portal.telnyx.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-cyan-400 text-sm hover:bg-virgo-navy-500 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Open Telnyx Portal
        </a>
        <p className="text-virgo-teal-600 text-xs mt-3">
          Porting typically takes 3–10 business days. Your current number remains active during the port.
        </p>
      </div>
    </div>
  );
}

export default TelnyxConfig;
